/**
 * Property catalogue (W1-03 core-model-ops; DESIGN.md §6.6, §6.7, §6.9, §6.13; rpe-format.md F§5.1, F§9).
 *
 * `PROP_SPECS` says, for each of the 12 line properties, how it is stored (summed eventLayers vs the single
 * `extended` set), its RPE list key, Phira's behaviour before the first key (`leadIn`), whether Phira's max_time and
 * Bezier registry scan it, the line kinds that use it, and the message key describing an ABSENT track.
 *
 * Lead-in note: speed is listed as 'extrapolate' (Phira's legacy integrator, the default). In rpe170 speed mode Phira
 * starts from speed 0 instead; callers that know the speed mode use `leadInRule(prop, speedMode)`.
 */

import type { ExtProp, LayeredProp, LineKind, LineProp, PropSpec, SpeedMode } from './types';

/** Every line property in canonical order (summed properties first, then the extended set). */
export const LINE_PROPS: readonly LineProp[] = Object.freeze([
  'moveX',
  'moveY',
  'rotate',
  'alpha',
  'speed',
  'scaleX',
  'scaleY',
  'color',
  'text',
  'incline',
  'paint',
  'gif',
]);

/** Properties summed over additive layers (RPE eventLayers). */
export const LAYERED_PROPS: readonly LayeredProp[] = Object.freeze(['moveX', 'moveY', 'rotate', 'alpha', 'speed']);

/** Properties of the single RPE `extended` set (layer 0 only). */
export const EXT_PROPS: readonly ExtProp[] = Object.freeze([
  'scaleX',
  'scaleY',
  'color',
  'text',
  'incline',
  'paint',
  'gif',
]);

const PROP_RANK: ReadonlyMap<string, number> = new Map(LINE_PROPS.map((p, i) => [p, i]));

/** True for the 12 line property names. */
export function isLineProp(x: unknown): x is LineProp {
  return typeof x === 'string' && PROP_RANK.has(x);
}

/** True for alpha, moveX, moveY, rotate and speed. */
export function isLayeredProp(p: LineProp): p is LayeredProp {
  return PROP_SPECS[p].layered;
}

/** Position of a property in `LINE_PROPS` (stable sort key for per-line track lists). */
export function propRank(p: LineProp): number {
  return PROP_RANK.get(p) ?? LINE_PROPS.length;
}

function spec(
  prop: LineProp,
  valueType: PropSpec['valueType'],
  leadIn: PropSpec['leadIn'],
  inMaxTime: boolean,
  appliesTo: PropSpec['appliesTo'],
): PropSpec {
  const layered = (LAYERED_PROPS as readonly string[]).includes(prop);
  return Object.freeze({
    prop,
    layered,
    valueType,
    rpeKey: `${prop}Events`,
    container: layered ? 'eventLayers' : 'extended',
    leadIn,
    inMaxTime,
    appliesTo,
    // Phira scans every list for its Bezier registry except speed (F§6.7).
    bezierScanned: prop !== 'speed',
    absentMessage: `model.absent.${prop}`,
  });
}

export const PROP_SPECS: Readonly<Record<LineProp, PropSpec>> = Object.freeze({
  moveX: spec('moveX', 'number', 'extrapolate', true, 'all'),
  moveY: spec('moveY', 'number', 'extrapolate', true, 'all'),
  rotate: spec('rotate', 'number', 'extrapolate', true, 'all'),
  alpha: spec('alpha', 'number', 'extrapolate', true, 'all'),
  // Floor speed drives the notes of every line kind; legacy integrator extrapolates (rpe170: 'zero', see leadInRule).
  speed: spec('speed', 'number', 'extrapolate', false, 'all'),
  // Length / image size / text size / UI size; on a paint line the brush circle is drawn under the line matrix
  // including its scale, with the x0.5 scaleX rule of line.png lines (R§3.7), so it stretches the brush.
  scaleX: spec('scaleX', 'number', 'extrapolate', true, 'all'),
  scaleY: spec('scaleY', 'number', 'extrapolate', true, 'all'),
  color: spec('color', 'rgb', 'default', false, 'all'),
  // Text and paint lists on textured lines are ignored by Phira (F§9.5); text loses to paint on line.png lines.
  text: spec('text', 'string', 'default', true, Object.freeze(['text'] as const)),
  // Incline is part of every line's note placement (frozen at t = 0, F§9.4).
  incline: spec('incline', 'number', 'default', false, 'all'),
  paint: spec('paint', 'number', 'default', false, Object.freeze(['paint'] as const)),
  gif: spec('gif', 'number', 'gifLoop', false, Object.freeze(['gif'] as const)),
});

/**
 * Phira's behaviour before the first key for a property under a speed mode: speed in rpe170 mode starts from 0
 * (Phira's 170 integrator), everything else as in `PROP_SPECS`.
 */
export function leadInRule(prop: LineProp, speedMode: SpeedMode): PropSpec['leadIn'] {
  if (prop === 'speed' && speedMode === 'rpe170') return 'zero';
  return PROP_SPECS[prop].leadIn;
}

/** True when the lead-in invariant (first key at beat 0 unless `importedLeadIn`) applies to the property. */
export function requiresLeadInKey(prop: LineProp): boolean {
  return PROP_SPECS[prop].leadIn === 'extrapolate';
}

/** True when Phira uses the property on a line of the given kind (others are kept but ignored, and flagged). */
export function propAppliesTo(prop: LineProp, kind: LineKind): boolean {
  const a = PROP_SPECS[prop].appliesTo;
  return a === 'all' || a.includes(kind);
}

/**
 * Message key (ns 'model') describing an absent track for a concrete line: `PROP_SPECS[prop].absentMessage`, refined
 * where Phira's default depends on the line kind (scale of normal / image / text / paint lines) or on the UI mover flag
 * (an attached HUD element without opacity keys is invisible).
 */
export function absentMessageKey(prop: LineProp, kind: LineKind, uiMover: boolean): string {
  switch (prop) {
    case 'alpha':
      return uiMover ? 'model.absent.alphaUiMover' : PROP_SPECS.alpha.absentMessage;
    case 'scaleX':
    case 'scaleY': {
      if (uiMover) return `model.absent.${prop}.ui`;
      if (kind === 'texture' || kind === 'gif') return `model.absent.${prop}.image`;
      if (kind === 'text') return `model.absent.${prop}.text`;
      if (kind === 'paint') return `model.absent.${prop}.paint`;
      return PROP_SPECS[prop].absentMessage;
    }
    case 'color':
      return kind === 'normal' && !uiMover ? PROP_SPECS.color.absentMessage : 'model.absent.color.white';
    default:
      return PROP_SPECS[prop].absentMessage;
  }
}
